import { call, put, select } from 'redux-saga/effects';
import { find } from 'lodash';
import { getErrorMessage } from '../../services/Errors';
import PaymentActions from '../actions/PaymentActions';
import ApplicationActions from '../actions/ApplicationActions';
import { getApplicationId, getData } from '../reducers/ApplicationReducer';
import { getCountries } from '../reducers/ConfigReducer';
import { getPaymentRecord, getQuote } from '../reducers/PaymentReducer';
import { uploadDocument } from './ApplicationSagas';
import { callApi } from './Api';

const {
  getFeesSuccess,
  getFeesFailure,
  getQuoteSuccess,
  getQuoteFailure,
  getPaymentMethodsSuccess,
  getPaymentMethodsFailure,
  paySuccess,
  payFailure,
  submitPaymentSuccess,
  submitPaymentFailure,
  sendPaymentLinkSuccess,
  sendPaymentLinkFailure,
  markForLocalPaymentSuccess,
  markForLocalPaymentFailure,
  getPaymentInfoSuccess,
  getPaymentInfoFailure,
} = PaymentActions;

const { successCheckZeroPayment, failureCheckZeroPayment } = ApplicationActions;
export function* getVisaFees(api, { visaTypeId }) {
  const response = yield call(callApi, api, 'getVisaFees', visaTypeId);
  if (response.ok) {
    yield put(getFeesSuccess(response.data));
  } else {
    yield put(
      getFeesFailure(
        getErrorMessage(response, 'Could not load application fees.')
      )
    );
  }
}

export function* createQuote(api, { fee, paymentMethodId }) {
  const data = yield select(getData);
  const countries = yield select(getCountries);
  const nationalityOriginId = data.nationalityOriginId;
  const country = find(countries, { uuid: nationalityOriginId }) || {};
  const visaType = fee.visatype || {};
  const payload = {
    applicationId: data.uuid,
    feeAmount: fee.amount,
    // feeCurrency: fee.currency,
    nationalityOriginCode: country.code,
    nationalityOriginId,
    nationalityOriginNameAltLangOne: country.nameAltLangOne,
    nationalityOriginNameAltLangTwo: country.nameAltLangTwo,
    nationalityOriginNameLangDefault: country.nameLangDefault,
    paymentMethodId,
    visatypeCode: visaType.code,
    visatypeId: visaType.uuid,
    visatypeNameAltLangOne: visaType.nameAltLangOne,
    visatypeNameAltLangTwo: visaType.nameAltLangTwo,
    visatypeNameLangDefault: visaType.nameLangDefault,
    visatypeNumberEntries: visaType.numberEntries,
    visatypeValidity: visaType.validity,
  };

  try {
    const response = yield call(callApi, api, 'getQuote', payload);
    if (response.ok) {
      yield put(getQuoteSuccess(response.data));
    } else {
      yield put(
        getQuoteFailure(getErrorMessage(response, 'Error getting quote.'))
      );
    }
  } catch (e) {
    yield put(getQuoteFailure(e.message));
  }
}

export function* pay(api, payload) {
  const response = yield call(callApi, api, 'pay', payload);
  if (response.ok) {
    if (response.redirectUrls) {
      window.location.replace(response.redirectUrls);
    }
    yield put(paySuccess(response.data));
  } else {
    yield put(payFailure(getErrorMessage(response, 'Error making payment.')));
  }
}

export function* zeroPay(api, {quote}) {

  const response = yield call(callApi, api, 'pay', {quoteId: quote});
  if (response.ok) {
    yield put(paySuccess(response.data));
    // yield call(history.push, `/payment/${uuid}/success`)
  } else {
    yield put(payFailure(getErrorMessage(response, 'Error making payment.')));
  }
}

export function* checkIsZeroPayment(api) {
  let applicationId = yield select(getApplicationId);
  const response = yield call(callApi, api, 'checkZeroPayment', applicationId);
  if (response.ok) {
    yield put(successCheckZeroPayment(response.data));
  } else {
    yield put(
      failureCheckZeroPayment(
        getErrorMessage(response, 'Could not check iz payment equal to zero.')
      )
    );
  }
}

export function* submitPayment(api, { status = 'APPROVED' }) {
  try {
    const { uuid } = yield select(getPaymentRecord);
    const response = yield call(callApi, api, 'submitPayment', uuid, status);
    if (response.ok) {
      yield put(submitPaymentSuccess(response.data));
    } else {
      yield put(
        submitPaymentFailure(
          getErrorMessage(response, 'Error submitting payment.')
        )
      );
    }
  } catch (e) {
    yield put(submitPaymentFailure(getErrorMessage(e.message)));
  }
}

export function* getPaymentMethods(api, { isLAO }) {
  try {
    const response = yield call(callApi, api, 'getPaymentMethods', isLAO);
    if (response.ok) {
      yield put(
        getPaymentMethodsSuccess(
          response.data.map((method) => ({
            value: method.uuid,
            label: method.name,
          }))
        )
      );
    } else {
      yield put(
        getPaymentMethodsFailure(
          getErrorMessage(response, 'Error getting payment methods')
        )
      );
    }
  } catch (e) {
    yield put(getPaymentMethodsFailure(e));
  }
}

export function* performCashPaymentFlow(api, action) {
  const {
    file,
    quoteId,
    cashPaymentReceiptNum,
    successPageUrl,
    failPageUrl,
    abortPageUrl,
  } = action;
  try {
    if (file) {
      yield call(uploadDocument, api, { file, documentTypeId: '12' });
    }
    yield call(pay, api, {
      quoteId,
      cashPaymentReceiptNum,
      successPageUrl,
      failPageUrl,
      abortPageUrl,
    });
  } catch (e) {
    console.log(e);
  }
}

export function* performMockPaymentFlow(api, { quoteId, push }) {
  try {
    yield call(pay, api, { quoteId });
    yield call(submitPayment, api, {});
    yield call(push, '/payment/success');
  } catch (e) {
    yield call(push, '/payment/failure');
    console.log(e);
  }
}

export function* sendPaymentLink(api, { uuid }) {
  try {
    const response = yield call(callApi, api, 'sendPaymentLink', uuid);
    if (response.ok) {
      yield put(sendPaymentLinkSuccess());
    } else {
      yield put(
        sendPaymentLinkFailure(
          getErrorMessage(response, 'Error sending payment link')
        )
      );
    }
  } catch (e) {
    yield put(sendPaymentLinkFailure(e.message));
  }
}

export function* markForLocalPayment(api, { uuid, push }) {
  try {
    const response = yield call(callApi, api, 'markForLocalPayment', uuid);
    if (response.ok) {
      yield put(markForLocalPaymentSuccess(response.data));
      yield call(push, `/payment/${uuid}/info`);
    } else {
      yield put(
        markForLocalPaymentFailure(
          getErrorMessage(
            response,
            'Error marking application as for local payment'
          )
        )
      );
    }
  } catch (e) {
    yield put(markForLocalPaymentFailure(e));
  }
}

export function* getPaymentInfo(api, { uuid }) {
  try {
    let response = yield call(callApi, api, 'getPaymentInfo', uuid);
    if (response.ok) {
      yield put(getPaymentInfoSuccess(response.data));
    } else {
      yield put(
        getPaymentInfoFailure(
          getErrorMessage(response, 'Error getting application payment info')
        )
      );
    }
  } catch (e) {
    yield put(getPaymentInfoFailure(e));
  }
}



// WEBPACK FOOTER //
// ./src/state/sagas/PaymentSagas.js